import { useSetRecoilState } from 'recoil';
import { TailSpin } from 'react-loader-spinner';
import FormatDropdown from '@/features/downloadMedia/mediaFormat/FormatDropdown';
import DownloadTab from '@/features/downloadMedia/downloadSidebar/individualDownload/DownloadTab';
import { ImageSelectorMedia } from '@/services/editorService';
import editIcon from '@/public/images/icons/edit--small.svg';
import useBatchDownloadTab from '@/features/downloadMedia/downloadSidebar/batchDownload/useBatchDownloadTab';
import { useLocalization } from '@/services/localizationService';
import { closeModalSelector } from '@/stores/modalStore';
import useDownload from '@/features/downloadMedia/downloadSidebar/useDownload';

export default function BatchDownloadTab({
  modalId,
  onThumbnailClick,
  onBackButtonClicked,
  shouldShowDownloadTab,
  selectedMediaForInfo,
}: {
  modalId: string;
  onThumbnailClick: (mediaForInfo: ImageSelectorMedia) => void;
  onBackButtonClicked: () => void;
  shouldShowDownloadTab: boolean;
  selectedMediaForInfo: ImageSelectorMedia;
}) {
  const { t, data } = useLocalization();
  const closeModal = useSetRecoilState(closeModalSelector);

  const { download, generateFormatFromOutput, reprocessIfNeeded } =
    useDownload();

  const {
    areMultipleFormatsSelected,
    batchDownloadFormat,
    isLoading,
    onBatchDownload,
    onFormatSelection,
    orderedMediasToDownload,
    taskToFormatAssociation,
  } = useBatchDownloadTab({ download, reprocessIfNeeded });

  return shouldShowDownloadTab ? (
    <DownloadTab
      media={selectedMediaForInfo}
      containsOnlyMediaInfo={orderedMediasToDownload.length > 1}
      onBackButtonClicked={onBackButtonClicked}
    />
  ) : (
    <>
      {isLoading ? (
        <div className="justify-center w-full flex mt-6">
          <TailSpin color="white" height="40" width="40" aria-label="loading" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-x-0.5 gap-y-3 mt-6 overflow-auto max-h-52 no-scrollbar">
            {orderedMediasToDownload.map((media) => {
              const originalFormat = generateFormatFromOutput(
                media.aiPipeline!,
                media.contentType!
              );
              const hasChangedFormat =
                originalFormat.label !==
                taskToFormatAssociation[media.id!].label;

              return (
                <div
                  key={media.id}
                  className="w-11/12 flex justify-center pointer-events-cursor hover:opacity-30 cursor-pointer relative"
                  onClick={() => onThumbnailClick(media)}
                >
                  {hasChangedFormat && (
                    <img
                      className="absolute top-1 end-1 w-[18px] h-[18px]"
                      {...editIcon}
                      alt="edited"
                    />
                  )}
                  <img
                    className="rounded-lg object-cover h-14"
                    src={media.outputUrl ? media.outputUrl : media.inputUrl}
                    alt={media.id}
                  />
                </div>
              );
            })}
          </div>
          <p className="R10 uppercase text-gray-400 font-bold tracking-wider mt-5">
            {t(data.result.batchFileType)}
          </p>
          <FormatDropdown
            downloadFormat={batchDownloadFormat}
            shouldShowMultipleFormatsLabel={areMultipleFormatsSelected}
            onFormatSelection={onFormatSelection}
          />
        </>
      )}
      <button
        type="button"
        disabled={isLoading}
        className="w-full h-9 btn btn--large btn--primary rounded-full border-0 mt-10"
        onClick={async () => {
          await onBatchDownload();
          closeModal(modalId);
        }}
      >
        <span className="M14">
          {t(data.result.batchDownload, {}, 'capitalize')}
        </span>
      </button>
    </>
  );
}
